/**
 * Company switcher for platform admins.
 *
 * Most users are pinned to a single Company (their JWT ``company_id``
 * claim). Platform admins can override per-request via
 * ``?company_id=<slug>`` — this module surfaces that capability as a
 * UI control.
 *
 * Behaviour:
 *   - Docks a control at the top of the sidebar nav (styled with the
 *     ui-kit nav-item language), visible only to platform admins
 *     (``user.role === 'admin'``). No floating chrome.
 *   - Click opens a ui-kit modal listing active Companies fetched from
 *     ``/api/admin/companies``, with a filter box.
 *   - Selecting a Company persists the override to ``localStorage`` and
 *     reloads the page so every in-flight cache refetches under the new
 *     scope.
 *   - The API client is patched at boot (``patchApiForCompanyOverride``)
 *     to append ``?company_id=<slug>`` to every request whenever an
 *     override is active.
 *   - "Use my Company" clears the override and reloads.
 */

import { api, store, getIcon, showModal } from '@isg/ui-kit';

const STORAGE_KEY = 'isg-pricing.companyOverride';
const NAV_ID = 'company-switcher-nav';

let companiesCache = null; // [{slug, name, country}]


// ── Storage helpers ───────────────────────────────────────────────────

export function getCompanyOverride() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function setCompanyOverride(slug) {
  try {
    if (slug) localStorage.setItem(STORAGE_KEY, slug);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable — override won't survive a reload */
  }
}


// ── API client patch ──────────────────────────────────────────────────

/**
 * Monkey-patch ``api.request`` to inject ``?company_id=<slug>`` on every
 * outbound request when an override is set. Call once at app boot
 * *before* any page makes API calls.
 *
 * Skips the auth + companies-list endpoints (we don't want a stale
 * override to fight the JWT refresh path or recursively scope the
 * switcher itself).
 */
export function patchApiForCompanyOverride() {
  if (api.__companyOverridePatched) return;
  api.__companyOverridePatched = true;

  const originalRequest = api.request.bind(api);

  api.request = function patchedRequest(path, options = {}) {
    const override = getCompanyOverride();
    if (!override) return originalRequest(path, options);

    // Skip auth + companies-list endpoints — these must use the JWT
    // claims directly without our override.
    if (typeof path === 'string') {
      if (path.startsWith('/auth/')
          || path.startsWith('/api/auth/')
          || path === '/admin/companies'
          || path === '/api/admin/companies') {
        return originalRequest(path, options);
      }
    }

    // Append company_id to the URL. If it's already present (e.g. an
    // explicit override at the call site), keep what the caller set.
    const sep = typeof path === 'string' && path.includes('?') ? '&' : '?';
    const patched = typeof path === 'string' && /[?&]company_id=/.test(path)
      ? path
      : `${path}${sep}company_id=${encodeURIComponent(override)}`;
    return originalRequest(patched, options);
  };
}


// ── UI rendering ──────────────────────────────────────────────────────

function _canSwitchCompany(user) {
  // Platform admins switch across every Company. Otherwise the switcher
  // appears for users whose ``company_access`` spans more than one brand
  // (e.g. ``is_group`` → ``['*']``, or an explicit multi-brand grant) —
  // mirrors the backend scope rule in ``app/services/scope.py``. A
  // single-brand user has nothing to switch to, so no chip.
  if (!user) return false;
  if (user.role === 'admin') return true;
  const access = Array.isArray(user.company_access) ? user.company_access : [];
  return access.includes('*') || access.length > 1;
}

async function _fetchCompanies() {
  if (companiesCache) return companiesCache;
  try {
    const data = await api.get('/admin/companies');
    companiesCache = (data && data.items) || [];
  } catch (err) {
    console.warn('Failed to load companies list', err);
    companiesCache = [];
  }
  return companiesCache;
}

/** Resolve a slug → display name, if the companies list is cached. */
function _nameFor(slug) {
  if (!slug) return slug;
  const hit = (companiesCache || []).find((c) => c.slug === slug);
  return hit ? hit.name : slug;
}

/**
 * Dock the switcher control at the top of the sidebar nav. Styled with
 * the ui-kit nav-item language so it reads as part of the chrome, not a
 * floating widget. Re-entrant: removes any prior instance first.
 */
function _renderSidebarControl(user) {
  const prior = document.getElementById(NAV_ID);
  if (prior) prior.remove();

  if (!_canSwitchCompany(user)) return;

  const navHost = document.querySelector('.sidebar .sidebar-nav');
  if (!navHost) return; // sidebar not mounted (e.g. login screen)

  const override = getCompanyOverride();
  const activeSlug = override || user.company_id || '—';
  const label = _nameFor(activeSlug);

  const section = document.createElement('div');
  section.className = 'nav-section';
  section.id = NAV_ID;
  section.setAttribute('role', 'group');
  section.setAttribute('aria-label', 'Workspace');

  // Override active → mimic .nav-item.active (accent left-border + darker
  // panel) so a non-default scope is impossible to miss.
  const overrideStyle = override
    ? 'background:var(--brand-800);border-left:2px solid var(--accent-500);'
    : '';
  const dotColor = override ? 'var(--accent-500)' : '#10b981';

  section.innerHTML = `
    <div class="nav-section-label" aria-hidden="true">Workspace</div>
    <button type="button" class="nav-item" id="cs-trigger"
            style="${overrideStyle}"
            aria-haspopup="dialog"
            title="${override ? `Viewing as ${_esc(activeSlug)} — click to switch` : `Viewing as your Company (${_esc(activeSlug)}) — click to switch`}">
      <span class="nav-item-icon" aria-hidden="true"
            style="display:inline-flex;align-items:center;justify-content:center">
        <span style="width:8px;height:8px;border-radius:50%;background:${dotColor};display:inline-block"></span>
      </span>
      <span class="nav-item-label" style="display:flex;flex-direction:column;line-height:1.25;min-width:0">
        ${override ? `<span style="font-size:0.625rem;text-transform:uppercase;letter-spacing:0.04em;color:var(--accent-500)">Viewing as</span>` : ''}
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${_esc(label)}</span>
      </span>
      <span aria-hidden="true" style="margin-left:auto;opacity:0.6">${getIcon('chevron-down', 14)}</span>
    </button>
  `;

  navHost.insertBefore(section, navHost.firstChild);
  section.querySelector('#cs-trigger').addEventListener('click', () => _openSwitcherModal(user));

  // First paint shows the slug; warm the companies list once in the
  // background, then re-render so the friendly name replaces it.
  if (companiesCache === null) {
    _fetchCompanies().then(() => _renderSidebarControl(store.get('user')));
  }
}

/** Open the ui-kit modal that lists Companies + a reset action. */
async function _openSwitcherModal(user) {
  const current = getCompanyOverride();

  const content = document.createElement('div');
  content.innerHTML = `
    <style>.cs-row:hover{background:var(--gray-100,#f3f4f6) !important;}</style>
    <input id="cs-filter" class="form-input" type="search" placeholder="Filter companies…"
           style="margin-bottom:var(--space-3)" autocomplete="off" />
    <div id="cs-list" style="max-height:50vh;overflow-y:auto;display:flex;flex-direction:column;gap:2px">
      <div style="padding:var(--space-3);color:var(--text-tertiary)">Loading…</div>
    </div>
    <div style="border-top:1px solid var(--border);margin-top:var(--space-3);padding-top:var(--space-3)">
      <button id="cs-clear" type="button" class="btn btn-ghost btn-sm"
              style="width:100%;justify-content:flex-start" ${current ? '' : 'disabled'}>
        ${getIcon('refresh-cw', 14)} Use my Company (${_esc(user.company_id || '—')})
      </button>
    </div>
  `;

  showModal({ title: 'Switch Company', content, size: 'sm' });

  const listEl = content.querySelector('#cs-list');
  const filterEl = content.querySelector('#cs-filter');
  content.querySelector('#cs-clear').addEventListener('click', () => {
    setCompanyOverride(null);
    window.location.reload();
  });

  const companies = await _fetchCompanies();

  const renderList = (q = '') => {
    const needle = q.trim().toLowerCase();
    const rows = companies.filter((c) =>
      !needle
      || (c.name || '').toLowerCase().includes(needle)
      || (c.slug || '').toLowerCase().includes(needle));

    if (!companies.length) {
      listEl.innerHTML = `<div style="padding:var(--space-3);color:var(--text-tertiary)">No companies found.</div>`;
      return;
    }
    if (!rows.length) {
      listEl.innerHTML = `<div style="padding:var(--space-3);color:var(--text-tertiary)">No matches.</div>`;
      return;
    }
    listEl.innerHTML = rows.map((c) => {
      const isCurrent = c.slug === current;
      return `
        <button type="button" class="cs-row" data-slug="${_esc(c.slug)}"
                style="width:100%;text-align:left;padding:var(--space-2) var(--space-3);background:${isCurrent ? 'var(--gray-100,#f3f4f6)' : 'transparent'};border:0;border-radius:var(--radius);cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:var(--space-2);color:var(--text-primary)">
          <span style="min-width:0">
            <strong style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${_esc(c.name)}</strong>
            <span style="color:var(--text-tertiary);font-size:var(--text-xs)">${_esc(c.slug)}${c.country ? ` · ${_esc(c.country)}` : ''}</span>
          </span>
          ${isCurrent ? `<span style="color:var(--accent-500,#BD1B44);flex-shrink:0">●</span>` : ''}
        </button>
      `;
    }).join('');

    listEl.querySelectorAll('button[data-slug]').forEach((btn) => {
      btn.addEventListener('click', () => {
        setCompanyOverride(btn.dataset.slug);
        window.location.reload();
      });
    });
  };

  renderList();
  filterEl.addEventListener('input', (e) => renderList(e.target.value));
  setTimeout(() => filterEl.focus(), 50);
}


// ── Public surface ────────────────────────────────────────────────────

/**
 * Mount the switcher control. Subscribes to the ``user`` store so the
 * control appears as soon as auth state lands (no race with main.js's
 * checkSession()).
 */
export function mountCompanySwitcher() {
  const user = store.get('user');
  if (user) _renderSidebarControl(user);
  store.subscribe('user', (u) => _renderSidebarControl(u));
}


// ── Helpers ───────────────────────────────────────────────────────────

function _esc(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
